import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Clone node off-screen so parent overflow/max-height does not clip html2canvas.
 * @param {HTMLElement} source
 */
function cloneForCapture(source) {
  const width = source.scrollWidth || source.offsetWidth;
  const clone = source.cloneNode(true);
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = [
    "position:fixed",
    "left:-100000px",
    "top:0",
    `width:${width}px`,
    "background:#fff",
    "overflow:visible",
    "z-index:-1",
  ].join(";");
  host.appendChild(clone);
  document.body.appendChild(host);
  return { host, clone, width };
}

function removeCaptureHost(host) {
  if (host?.parentNode) {
    host.parentNode.removeChild(host);
  }
}

async function waitForImages(el, timeoutMs = 10000) {
  const imgs = el.querySelectorAll("img");
  if (imgs.length === 0) return;
  await Promise.all(
    Array.from(imgs).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalHeight !== 0) {
            resolve();
            return;
          }
          const onDone = () => resolve();
          img.addEventListener("load", onDone, { once: true });
          img.addEventListener("error", onDone, { once: true });
          setTimeout(onDone, timeoutMs);
        }),
    ),
  );
}

async function inlineImagesForCapture(root) {
  const originalSrcs = [];
  const imgs = root.querySelectorAll("img");

  for (const img of imgs) {
    const src = img.getAttribute("src") || img.src;
    if (!src || src.startsWith("data:")) continue;
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error("Image read failed"));
        fr.readAsDataURL(blob);
      });
      originalSrcs.push({ img, src });
      img.setAttribute("src", dataUrl);
      img.src = dataUrl;
    } catch {
      /* keep original src */
    }
  }

  return () => {
    for (const { img, src } of originalSrcs) {
      if (img && src) {
        img.setAttribute("src", src);
        img.src = src;
      }
    }
  };
}

/**
 * Draw canvas slices onto A4 pages (one page per slice).
 */
export function addCanvasToPaginatedPdf(canvas, pdf, { marginMm = 10 } = {}) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const printableWidth = pageWidth - marginMm * 2;
  const printableHeight = pageHeight - marginMm * 2;

  const sliceHeightPx = Math.floor(
    (printableHeight / printableWidth) * canvas.width,
  );

  let pageIndex = 0;
  let sourceY = 0;

  while (sourceY < canvas.height) {
    const slicePx = Math.min(sliceHeightPx, canvas.height - sourceY);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = slicePx;
    const ctx = pageCanvas.getContext("2d");
    if (!ctx) break;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      slicePx,
      0,
      0,
      canvas.width,
      slicePx,
    );

    const imgData = pageCanvas.toDataURL("image/png");
    const sliceHeightMm = (slicePx * printableWidth) / canvas.width;

    if (pageIndex > 0) {
      pdf.addPage();
    }
    pdf.addImage(
      imgData,
      "PNG",
      marginMm,
      marginMm,
      printableWidth,
      sliceHeightMm,
    );

    sourceY += slicePx;
    pageIndex += 1;
  }

  return pageIndex;
}

/**
 * Capture an element and save as multi-page A4 PDF.
 * @param {HTMLElement} element
 * @param {string} filename
 */
export async function exportElementToPaginatedPdf(
  element,
  filename = "report.pdf",
) {
  if (!element) {
    throw new Error("Nothing to export");
  }

  const { host, clone } = cloneForCapture(element);
  const restoreImages = await inlineImagesForCapture(clone);

  try {
    await waitForImages(clone);
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    );

    const w = clone.scrollWidth || clone.offsetWidth;
    const h = clone.scrollHeight || clone.offsetHeight;

    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: w,
      height: h,
      windowWidth: w,
      windowHeight: h,
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pages = addCanvasToPaginatedPdf(canvas, pdf);
    if (pages === 0) {
      throw new Error("PDF export produced no pages");
    }
    pdf.save(filename);
  } finally {
    restoreImages();
    removeCaptureHost(host);
  }
}
